/**
 * Shared state of the hull material family: data, per-object and global uniforms, the band tables and the
 * authored texture sets. Built once per library; every slot material reads from it.
 */
import { Color, Vector4, type Node, type Texture, type UniformNode } from 'three/webgpu'
import { uniform, uniformArray } from 'three/tsl'
import type { LoadedTextureSet } from '../../../assets/loader'
import type { FactionId } from '../../contracts'
import type { MaterialsFile, TuningDefaults } from '../data'
import { BAND_CELLS, TRIM_LAYOUT, buildBandTable, isAuthoredSet } from './layout'
import { LIVERY_KEY, LIVERY_ROW, LIVERY_ROWS } from './livery'
import { createKeyUniform, createObjectUniforms, type ObjectUniforms, type Vec4Uniform } from './objectData'

export type FloatUniform = UniformNode<'float', number>
export type ColorUniform = UniformNode<'color', Color>
type FloatNode = Node<'float'>
type V3 = Node<'vec3'>

/** Live tuning multipliers (dev panel / lookdev "Export JSON" → materials.json `tuning`). */
export type TuneUniforms = { readonly [K in keyof TuningDefaults]: FloatUniform }

export interface AuthoredSets {
  readonly trimReach: LoadedTextureSet | null
  readonly trimFirstRush: LoadedTextureSet | null
  readonly detail: LoadedTextureSet | null
  readonly decals: LoadedTextureSet | null
}

export interface HullContext {
  readonly data: MaterialsFile
  readonly obj: ObjectUniforms
  /** (key light direction in view space, key on 0/1), per render. */
  readonly key: Vec4Uniform
  readonly time: FloatUniform
  readonly tune: TuneUniforms
  readonly bandA: ReturnType<typeof uniformArray<'vec4'>>
  readonly bandB: ReturnType<typeof uniformArray<'vec4'>>
  readonly sets: AuthoredSets
}

/** Geometry variant of a slot material: which mask and UV conventions the mesh follows (edgeBake.ts). */
export interface Variant {
  readonly baked: boolean
  readonly trimmed: boolean
}

export function variantKey(v: Variant): string {
  return `${v.baked ? 'b' : 'f'}${v.trimmed ? 't' : 'u'}`
}

export const HERO_VARIANT: Variant = { baked: true, trimmed: true }

/** Which authored trim set a trimmed hull samples: the first_rush look has its own; every other look shares the Reach set. */
export type TrimFamily = 'reach' | 'first_rush'

export function trimFamilyOf(look: FactionId): TrimFamily {
  return look === 'first_rush' ? 'first_rush' : 'reach'
}

export function authoredSets(sets: ReadonlyMap<string, LoadedTextureSet>, data: MaterialsFile): AuthoredSets {
  const pick = (id: string): LoadedTextureSet | null => {
    const s = sets.get(id)
    return isAuthoredSet(s, data.trim.authoredMinPx) ? s : null
  }
  return { trimReach: pick('tex.trim.reach'), trimFirstRush: pick('tex.trim.first_rush'), detail: pick('tex.detail.metal'), decals: pick('tex.decals') }
}

/** Every map of the authored sets (anisotropy by preset, contract §4.5). */
export function authoredMaps(sets: AuthoredSets): Texture[] {
  const out: Texture[] = []
  for (const s of [sets.trimReach, sets.trimFirstRush, sets.detail, sets.decals]) {
    if (!s) continue
    for (const t of Object.values(s.maps)) if (t && !out.includes(t)) out.push(t)
  }
  return out
}

export function createTuneUniforms(t: TuningDefaults): TuneUniforms {
  return {
    wear: uniform(t.wear),
    grime: uniform(t.grime),
    soot: uniform(t.soot),
    rim: uniform(t.rim),
    clearcoat: uniform(t.clearcoat),
    clearcoatRoughness: uniform(t.clearcoatRoughness),
    panelLines: uniform(t.panelLines),
  }
}

export function createHullContext(data: MaterialsFile, sets: ReadonlyMap<string, LoadedTextureSet>, time: FloatUniform): HullContext {
  const table = buildBandTable(TRIM_LAYOUT, data)
  const a: Vector4[] = []
  const b: Vector4[] = []
  for (let i = 0; i < BAND_CELLS; i++) {
    a.push(new Vector4().fromArray(table, i * 8))
    b.push(new Vector4().fromArray(table, i * 8 + 4))
  }
  return {
    data,
    obj: createObjectUniforms(data),
    key: createKeyUniform(),
    time,
    tune: createTuneUniforms(data.tuning),
    bandA: uniformArray(a, 'vec4'),
    bandB: uniformArray(b, 'vec4'),
    sets: authoredSets(sets, data),
  }
}

/**
 * The style guide §5.2 livery inputs (same names for every look). Since M6 P23 they are per-object: one shared set of
 * vec4 uniforms reads the mesh's packed livery (`userData[LIVERY_KEY]`, written by applyShipLook/applyStaticLook),
 * so every look shares one material per slot and a livery swap never recompiles. Colours are linear.
 */
export interface PaletteUniforms {
  /** The LIVERY_ROWS per-object vec4 uniforms behind the fields below (hull/livery.ts layout). */
  readonly rows: readonly Vec4Uniform[]
  readonly uPaintPrimary: V3
  readonly uPaintSecondary: V3
  readonly uPaintAccent: V3
  readonly uMetalTint: V3
  readonly uPrimer: V3
  readonly uDecalInk: V3
  readonly uDecalInkAlt: V3
  readonly uHazardA: V3
  readonly uHazardB: V3
  readonly uEngineCore: V3
  readonly uSensor: V3
  readonly uGrime: V3
  readonly uGlassTint: V3
  readonly uGlassGlow: V3
  readonly uEmissiveIdle: V3
  /** Paint roughness of the primary, secondary and accent zones. */
  readonly uPaintRoughness: V3
  readonly uWear: FloatNode
  readonly uGrimeAmount: FloatNode
  readonly uSootAmount: FloatNode
  readonly uOxidation: FloatNode
  readonly uGloss: FloatNode
  readonly uBleach: FloatNode
  readonly uPitting: FloatNode
  readonly uMetalRoughness: FloatNode
  readonly uGlassGlowY: FloatNode
  readonly uEmissiveIdleY: FloatNode
  /** Kit wear chip look (D-341): the under-layer per paint zone and the chip shape. */
  readonly uChipUnderPrimary: V3
  readonly uChipUnderSecondary: V3
  readonly uChipUnderAccent: V3
  readonly uChipEdge: FloatNode
  readonly uChipBareDepth: FloatNode
  readonly uChipFreqMul: FloatNode
  readonly uChipContrastLo: FloatNode
  readonly uChipContrastHi: FloatNode
  /** 1: the contrast smoothstep applies to ECL_Mask G; 0: raw G (legacy). */
  readonly uChipContrastOn: FloatNode
  readonly uChipLarge: FloatNode
  /** Tonal break-up (amount 0 = off). */
  readonly uTonalAmount: FloatNode
  readonly uTonalDesaturate: FloatNode
  readonly uTonalFrequency: FloatNode
  // Look-independent globals (materials.json `globals`), shared plain uniforms.
  readonly uSoot: ColorUniform
  readonly uRimColor: ColorUniform
  readonly uRimStrength: FloatUniform
  readonly uRimPower: FloatUniform
}

function named<T extends { setName(name: string): T }>(u: T, name: string): T {
  return u.setName(name)
}

/** The shared per-object livery uniforms (one set per library) plus the global colours. */
export function createPalette(data: MaterialsFile, fallback: Float32Array): PaletteUniforms {
  const g = data.globals
  const rows: Vec4Uniform[] = []
  for (let i = 0; i < LIVERY_ROWS; i++) {
    rows.push(
      named(
        uniform(new Vector4()).onObjectUpdate((frame, self) => {
          const o = frame.object
          const d = o ? (o.userData[LIVERY_KEY] as Float32Array | undefined) : undefined
          self.value.fromArray(d ?? fallback, i * 4)
          return undefined
        }),
        `uLivery${i}`,
      ),
    )
  }
  const rgb = (r: number): V3 => rows[r].xyz
  const w = (r: number): FloatNode => rows[r].w
  const R = LIVERY_ROW
  return {
    rows,
    uPaintPrimary: rgb(R.paintPrimary),
    uPaintSecondary: rgb(R.paintSecondary),
    uPaintAccent: rgb(R.paintAccent),
    uMetalTint: rgb(R.metalTint),
    uPrimer: rgb(R.primer),
    uDecalInk: rgb(R.decalInk),
    uDecalInkAlt: rgb(R.decalInkAlt),
    uHazardA: rgb(R.hazardA),
    uHazardB: rgb(R.hazardB),
    uEngineCore: rgb(R.engineCore),
    uSensor: rgb(R.sensor),
    uGrime: rgb(R.grime),
    uGlassTint: rgb(R.glassTint),
    uGlassGlow: rgb(R.glassGlow),
    uEmissiveIdle: rgb(R.emissiveIdle),
    uPaintRoughness: rgb(R.paintRoughness),
    uWear: w(R.paintPrimary),
    uGrimeAmount: w(R.paintSecondary),
    uSootAmount: w(R.paintAccent),
    uOxidation: w(R.metalTint),
    uGloss: w(R.primer),
    uBleach: w(R.decalInk),
    uPitting: w(R.decalInkAlt),
    uMetalRoughness: w(R.hazardA),
    uGlassGlowY: w(R.hazardB),
    uEmissiveIdleY: w(R.engineCore),
    uChipUnderPrimary: rgb(R.chipUnderPrimary),
    uChipUnderSecondary: rgb(R.chipUnderSecondary),
    uChipUnderAccent: rgb(R.chipUnderAccent),
    uChipEdge: w(R.chipUnderPrimary),
    uChipBareDepth: w(R.chipUnderSecondary),
    uChipFreqMul: w(R.chipUnderAccent),
    uChipContrastLo: rows[R.chipShape].x,
    uChipContrastHi: rows[R.chipShape].y,
    uChipContrastOn: rows[R.chipShape].z,
    uChipLarge: w(R.chipShape),
    uTonalAmount: w(R.sensor),
    uTonalDesaturate: w(R.grime),
    uTonalFrequency: w(R.glassTint),
    uSoot: named(uniform(new Color(g.soot)), 'uSoot'),
    uRimColor: named(uniform(new Color(g.rimColor)), 'uRimColor'),
    uRimStrength: named(uniform(g.rimStrength), 'uRimStrength'),
    uRimPower: named(uniform(g.rimPower), 'uRimPower'),
  }
}
